// src/admin/components/assets/AssetModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import AdminModal from '../common/AdminModal';
import AssetFormFields from './AssetFormFields';

const EMPTY_FORM = {
  asset_name: '',
  asset_make: '',
  asset_model: '',
  asset_serial_number: '',
  asset_warranty_end_date: '',
  asset_ext_id: '',
  street_address: '',
  city: '',
};

function buildFormFromAsset(asset) {
  if (!asset) return EMPTY_FORM;
  return {
    asset_name: asset.asset_name || '',
    asset_make: asset.asset_make || '',
    asset_model: asset.asset_model || '',
    asset_serial_number: asset.asset_serial_number || '',
    asset_warranty_end_date: asset.asset_warranty_end_date 
      ? asset.asset_warranty_end_date.split('T')[0] 
      : '',
    asset_ext_id: asset.asset_ext_id || '',
    street_address: asset.street_address || '',
    city: asset.city || '',
  };
}

/**
 * AssetModal - Modal for creating or editing assets
 * 
 * Props:
 *  - show: boolean - whether to show the modal
 *  - onClose: function - called when modal is closed
 *  - asset: object|null - existing asset for editing, null for creating
 *  - onSave: function(assetData) - called when save button is clicked
 *  - saving: boolean - whether save is in progress
 *  - error: string|null - error message to display
 */
export default function AssetModal({ show, onClose, asset, onSave, saving = false, error = null }) {
  const isEditMode = !!asset;
  const [assetForm, setAssetForm] = useState(EMPTY_FORM);
  
  // Track open count to use as key for form remount
  const openCountRef = useRef(0);
  const [formKey, setFormKey] = useState(0);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      openCountRef.current += 1;
      setFormKey(openCountRef.current);
      setAssetForm(buildFormFromAsset(asset));
    }
  }, [show, asset]);

  const handleSave = () => {
    onSave(assetForm);
  };

  const isValid = assetForm.asset_name?.trim();

  const footer = (
    <div className="d-flex justify-content-between w-100">
      <button 
        type="button" 
        className="btn btn-link text-muted" 
        onClick={onClose}
        disabled={saving}
      >
        <i className="bi bi-arrow-left me-1" /> Cancel
      </button>
      <button 
        type="button" 
        className="btn btn-primary" 
        onClick={handleSave}
        disabled={!isValid || saving}
      >
        {saving ? (
          <>
            <span className="spinner-border spinner-border-sm me-2" />
            Saving...
          </>
        ) : (
          isEditMode ? 'Save Changes' : 'Create Asset'
        )}
      </button>
    </div>
  );

  return (
    <AdminModal
      show={show}
      onClose={onClose}
      title={isEditMode ? 'Edit Asset' : 'New Asset'}
      footer={footer}
    >
      <div className="mb-4">
        <h6 className="text-uppercase text-muted small mb-3">Asset Information</h6>
        <AssetFormFields
          key={formKey}
          assetForm={assetForm}
          onChange={setAssetForm}
          mode={isEditMode ? 'edit' : 'create'}
        />
      </div>
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
    </AdminModal>
  );
}

AssetModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  asset: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  error: PropTypes.string,
};

AssetModal.defaultProps = {
  asset: null,
  saving: false,
  error: null,
};
